/**
 * Steps of the standard space scale (`--space-*`), plus `none` for zero.
 * Components that let authors pick a spacing take one of these names, so a
 * tenant theme changes the spacing by re-valuing the shared step.
 */
export const SPACE_STEPS = [
  'none',
  'min',
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  'max',
] as const;
export type SpaceStep = (typeof SPACE_STEPS)[number];

type SpaceClasses = Readonly<Record<SpaceStep, string>>;

// Tailwind only generates classes it finds written out, so each map spells
// every class in full.
export const SPACE_GAP: SpaceClasses = {
  none: 'gap-0',
  min: 'gap-min',
  xxs: 'gap-xxs',
  xs: 'gap-xs',
  sm: 'gap-sm',
  md: 'gap-md',
  lg: 'gap-lg',
  xl: 'gap-xl',
  '2xl': 'gap-2xl',
  '3xl': 'gap-3xl',
  max: 'gap-max',
};

export const SPACE_GAP_X: SpaceClasses = {
  none: 'gap-x-0',
  min: 'gap-x-min',
  xxs: 'gap-x-xxs',
  xs: 'gap-x-xs',
  sm: 'gap-x-sm',
  md: 'gap-x-md',
  lg: 'gap-x-lg',
  xl: 'gap-x-xl',
  '2xl': 'gap-x-2xl',
  '3xl': 'gap-x-3xl',
  max: 'gap-x-max',
};

export const SPACE_GAP_LG: SpaceClasses = {
  none: 'lg:gap-0',
  min: 'lg:gap-min',
  xxs: 'lg:gap-xxs',
  xs: 'lg:gap-xs',
  sm: 'lg:gap-sm',
  md: 'lg:gap-md',
  lg: 'lg:gap-lg',
  xl: 'lg:gap-xl',
  '2xl': 'lg:gap-2xl',
  '3xl': 'lg:gap-3xl',
  max: 'lg:gap-max',
};

export const SPACE_PX: SpaceClasses = {
  none: 'px-0',
  min: 'px-min',
  xxs: 'px-xxs',
  xs: 'px-xs',
  sm: 'px-sm',
  md: 'px-md',
  lg: 'px-lg',
  xl: 'px-xl',
  '2xl': 'px-2xl',
  '3xl': 'px-3xl',
  max: 'px-max',
};

export const SPACE_PY: SpaceClasses = {
  none: 'py-0',
  min: 'py-min',
  xxs: 'py-xxs',
  xs: 'py-xs',
  sm: 'py-sm',
  md: 'py-md',
  lg: 'py-lg',
  xl: 'py-xl',
  '2xl': 'py-2xl',
  '3xl': 'py-3xl',
  max: 'py-max',
};

export const SPACE_PX_MD: SpaceClasses = {
  none: 'md:px-0',
  min: 'md:px-min',
  xxs: 'md:px-xxs',
  xs: 'md:px-xs',
  sm: 'md:px-sm',
  md: 'md:px-md',
  lg: 'md:px-lg',
  xl: 'md:px-xl',
  '2xl': 'md:px-2xl',
  '3xl': 'md:px-3xl',
  max: 'md:px-max',
};

export const SPACE_PX_LG: SpaceClasses = {
  none: 'lg:px-0',
  min: 'lg:px-min',
  xxs: 'lg:px-xxs',
  xs: 'lg:px-xs',
  sm: 'lg:px-sm',
  md: 'lg:px-md',
  lg: 'lg:px-lg',
  xl: 'lg:px-xl',
  '2xl': 'lg:px-2xl',
  '3xl': 'lg:px-3xl',
  max: 'lg:px-max',
};

export const SPACE_PT: SpaceClasses = {
  none: 'pt-0',
  min: 'pt-min',
  xxs: 'pt-xxs',
  xs: 'pt-xs',
  sm: 'pt-sm',
  md: 'pt-md',
  lg: 'pt-lg',
  xl: 'pt-xl',
  '2xl': 'pt-2xl',
  '3xl': 'pt-3xl',
  max: 'pt-max',
};

export const SPACE_PB: SpaceClasses = {
  none: 'pb-0',
  min: 'pb-min',
  xxs: 'pb-xxs',
  xs: 'pb-xs',
  sm: 'pb-sm',
  md: 'pb-md',
  lg: 'pb-lg',
  xl: 'pb-xl',
  '2xl': 'pb-2xl',
  '3xl': 'pb-3xl',
  max: 'pb-max',
};

export const SPACE_PL: SpaceClasses = {
  none: 'pl-0',
  min: 'pl-min',
  xxs: 'pl-xxs',
  xs: 'pl-xs',
  sm: 'pl-sm',
  md: 'pl-md',
  lg: 'pl-lg',
  xl: 'pl-xl',
  '2xl': 'pl-2xl',
  '3xl': 'pl-3xl',
  max: 'pl-max',
};

export const SPACE_MT: SpaceClasses = {
  none: 'mt-0',
  min: 'mt-min',
  xxs: 'mt-xxs',
  xs: 'mt-xs',
  sm: 'mt-sm',
  md: 'mt-md',
  lg: 'mt-lg',
  xl: 'mt-xl',
  '2xl': 'mt-2xl',
  '3xl': 'mt-3xl',
  max: 'mt-max',
};
